/**
 * Napojenie realtime kanala na React.
 *
 * Provider len drzi spojenie nazive, kym je pouzivatel prihlaseny. Samotne
 * spojenie zije v features/realtimeClient.js ako modulovy singleton -- vdaka
 * tomu ho StrictMode dvojitym mountom nerozbije.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import * as realtime from "../features/realtimeClient";

const RealtimeContext = createContext({ state: "idle" });

export function RealtimeProvider({ children }) {
  const { isLoggedIn, userRole } = useAuth();
  const [state, setState] = useState(realtime.getState());

  useEffect(() => {
    // Zakaznik realtime kanal nema (R5) -- `POST /api/events/ticket` mu vrati
    // 403 a klient by ho skusal donekonecna. Nema mu co chodit: svoje
    // objednavky vidi vtedy, ked si obrazovku otvori.
    if (!isLoggedIn || userRole === "CUSTOMER") return undefined;
    const unsub = realtime.subscribeState(setState);
    realtime.acquire();
    return () => {
      unsub();
      realtime.release();
    };
  }, [isLoggedIn, userRole]);

  return (
    <RealtimeContext.Provider value={{ state }}>{children}</RealtimeContext.Provider>
  );
}

/** Stav kanala: idle | connecting | open | polling | expired | off */
export function useRealtimeState() {
  return useContext(RealtimeContext).state;
}

/**
 * Rozhodne, ci sa dana udalost tyka konkretneho pohladu.
 *
 * Filtruje sa na KLIENTOVI zamerne: server by inak musel sledovat, aky datum
 * a trasu ma kazdy klient prave zvoleny -- teda subscription protokol a stav,
 * ktory sa moze rozjst. Pri desiatkach klientov su usetrene bajty bezvyznamne.
 */
export function matchesOrderScope(evt, { date, dateFrom, dateTo, routeId, customerId } = {}) {
  if (!evt) return false;

  // Dobiehanie po vypadku musi prejst vzdy -- nevieme, co sme zmeskali.
  if (evt.entity === "resync") return true;
  // Zmena trasy (premenovanie, prehodenie vodica) sa moze dotknut kazdeho pohladu.
  if (evt.entity === "route") return true;

  if (date && evt.date && evt.date !== date) return false;
  if (dateFrom && dateTo && evt.date && (evt.date < dateFrom || evt.date > dateTo)) return false;

  if (
    routeId &&
    routeId !== "all" &&
    Array.isArray(evt.routeIds) &&
    evt.routeIds.length > 0 &&
    !evt.routeIds.includes(Number(routeId))
  ) {
    return false;
  }

  if (customerId && evt.customerId && Number(evt.customerId) !== Number(customerId)) return false;

  return true;
}

/**
 * Prihlasi obrazovku na odber. `onChange` sa zavola NAJVIAC RAZ za okno
 * `debounceMs`, a to trailing.
 *
 * Poradie je podstatne: `filter` bezi SYNCHRONNE na kazdej udalosti, este pred
 * debounce. Keby bezal az po nom, davka koniaca nerelevantnou udalostou by
 * zamaskovala relevantnu skorsiu.
 *
 * `filter` aj `onChange` sa drzia v refoch, takze meniaci sa datum alebo trasa
 * nesposobia odhlasenie a nove prihlasenie odberu.
 */
/**
 * Jednotlivé udalosti aj s telom, bez zdržania.
 *
 * `useOrdersRealtime` nižšie udalosti **zlucí** a zavolá len jedno prečítanie —
 * to je správne pre obnovu zoznamu, ale nepoužiteľné tam, kde záleží na každej
 * udalosti zvlášť: napísať dispečerovi, že prišli DVE webové objednávky (R38).
 */
export function useOrderEvents({ filter, onEvent, enabled = true }) {
  const filterRef = useRef(filter);
  const onEventRef = useRef(onEvent);
  filterRef.current = filter;
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    return realtime.subscribe((evt) => {
      if (filterRef.current && !filterRef.current(evt)) return;
      onEventRef.current?.(evt);
    });
  }, [enabled]);
}

export function useOrdersRealtime({ filter, onChange, debounceMs = 400, enabled = true }) {
  const filterRef = useRef(filter);
  const onChangeRef = useRef(onChange);
  filterRef.current = filter;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;
    const unsub = realtime.subscribe((evt) => {
      const zaujima = filterRef.current ? filterRef.current(evt) : true;
      if (!zaujima) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        onChangeRef.current?.();
      }, debounceMs);
    });

    return () => {
      clearTimeout(timer);
      unsub();
    };
  }, [enabled, debounceMs]);
}
